import * as R from 'ramda'
import type { Constant, Nil } from '@pbt/pbt-ui-components'
import { DateUtils, LanguageUtils, moment } from '@pbt/pbt-ui-components'

import { RxPrescriptionOrigin, RxType } from '~/api/graphql/generated/types'
import {
  getQtyRequested,
  hasPartNumber,
} from '~/components/dashboard/clients/timeline/prescriptions/rxTracker/getQtyRequested'
import { getOrderDisplayStatus } from '~/components/dashboard/clients/utils/orderStatusChipUtils'
import type { OrderDisplayStatus } from '~/constants/orderStatusChip'
import { PrescriptionStates, PrescriptionType } from '~/constants/prescription'
import { OrderType } from '~/constants/SOAPStates'
import i18n from '~/locales/i18n'
import type { PrescriptionTimelineItem, RxCardItem } from '~/types'
import type {
  Prescription,
  PrescriptionV2,
} from '~/types/entities/prescriptions'
import {
  convertPrescriptionV2ToPrescription,
  getPrescriptionType,
  getPrescriptionTypeLabel,
  getRxType,
} from '~/utils/prescription'
import { getRemainingRefillsCount } from '~/utils/prescriptionUtils'

import type { OrderTableRow } from './components/OrderTable'

const toTrimmedString = (value: string | Nil): string =>
  value == null ? '' : String(value).trim()

export const EMPTY_FIELD_PLACEHOLDER = '--'

/** Accepts rxUsages from PrescriptionV2 (GraphQL) or order table (RxUsageWithShipments[]). */
type HasRxUsagesValue =
  | { rxUsages?: Array<object | null> | null }
  | null
  | undefined

export const hasRxUsages = (value: HasRxUsagesValue): boolean =>
  Array.isArray(value?.rxUsages) && value.rxUsages.length > 0

/** True when the prescription has data needed for order table (qty + shipping): rx partNumber (string), refills, or rxUsages. */
export function hasOrderTableLoadedData(p: unknown): boolean {
  if (!p || typeof p !== 'object') return false
  const o = p as Record<string, unknown>
  return (
    hasPartNumber(o as { partNumber?: string }) ||
    (Array.isArray(o.refills) && o.refills.length > 0) ||
    hasRxUsages(o)
  )
}

export const formatValueOrPlaceholder = (value: string | Nil): string => {
  const s = toTrimmedString(value)
  return s || EMPTY_FIELD_PLACEHOLDER
}

type NullableType<T> = T | Nil
type NullableString = NullableType<string>
type NullablePrescriptionType = NullableType<PrescriptionType>

export type DisplayField = {
  boldValue?: boolean
  label: string
  onClick?: () => void
  parentPrescription?: Prescription
  prescription?: Prescription
  relativeTime?: string | null
  secondaryText?: string | null
  tooltipText?: string
  /** When true, secondaryText uses date label styling. When false (default), uses regular text styling. */
  useSecondaryTextStyle?: boolean
  value: string
}

export interface PrescriptionFlags {
  isCanceled: boolean
  isChewy: boolean
  isDeclined: boolean
  isPendingVetApproval: boolean
  isScriptOut: boolean
  stateName: string | null
}

export interface GetRxFieldsOptions {
  DrugDeliveryMethod?: Constant[]
  DrugStrengthColor?: Constant[]
  DrugStrengthUnit?: Constant[]
  InventoryProductSizeUnit?: Constant[]
  ProductForm?: Constant[]
  isOrderedDrug?: boolean
  onDispenseRefill?: () => void
  prescriptionProductSizeUnit?: Constant[]
  prescriptionStates?: Constant[]
  productSizeUnit?: Constant[]
  vetDietConstants?: import('~/components/dashboard/clients/timeline/prescriptions/rxTracker/getQtyRequested').VetDietConstants
}

type OrderTableFieldRowOptions = Pick<
  GetRxFieldsOptions,
  | 'DrugDeliveryMethod'
  | 'DrugStrengthColor'
  | 'DrugStrengthUnit'
  | 'InventoryProductSizeUnit'
  | 'ProductForm'
  | 'prescriptionProductSizeUnit'
  | 'productSizeUnit'
  | 'prescriptionStates'
  | 'vetDietConstants'
>

export const getPrescriptionFlags = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  prescriptionStates?: Constant[],
): PrescriptionFlags => {
  const stateName =
    rx.stateId && prescriptionStates
      ? (prescriptionStates.find((s) => s.id === rx.stateId)?.name ?? null)
      : null

  const isDeclined = stateName === PrescriptionStates.DECLINED
  const isCanceled = stateName === PrescriptionStates.CANCELED
  const isPendingVetApproval =
    stateName === PrescriptionStates.PENDING_VET_APPROVAL

  return {
    stateName,
    isDeclined,
    isCanceled,
    isPendingVetApproval,
    isScriptOut: prescriptionType === PrescriptionType.SCRIPT_OUT,
    isChewy: prescriptionType === PrescriptionType.CHEWY,
  }
}

/**
 * True when the card should show the field set that includes Qty Requested (order date, fulfillment, qty requested).
 * Used for: declined, pending vet approval (and not canceled), and outside pharmacy (SCRIPT_OUT).
 */
export const shouldSuppressFields = (flags: PrescriptionFlags): boolean =>
  ((flags.isDeclined || flags.isPendingVetApproval) && !flags.isCanceled) ||
  flags.isScriptOut

const DateFormatters = {
  date: (value: NullableString): string =>
    DateUtils.formatDate(value, false, 'MM/DD/YYYY'),
  dateTime: (value: NullableString): string =>
    DateUtils.formatDate(value, false, 'MM/DD/YYYY | h:mmA'),
}

const formatDateOrPlaceholder = (
  value: NullableString,
  format: (v: NullableString) => string,
): string => {
  const s = toTrimmedString(value)
  return s ? format(value) : formatValueOrPlaceholder(value)
}

const formatDateValueOrPlaceholder = (
  value: NullableString,
  format: (v: string) => string,
): string => {
  const trimmed = toTrimmedString(value)
  if (!trimmed) return EMPTY_FIELD_PLACEHOLDER

  const formatted = format(trimmed)

  if (!formatted || /invalid\s*date/i.test(formatted)) {
    return EMPTY_FIELD_PLACEHOLDER
  }

  return formatted
}

export const computeRelativeTime = (
  date: NullableString,
  timezone: string,
): string | null => {
  if (!date) return null
  const m = moment.tz(date, timezone)
  return m.isValid() ? m.fromNow() : null
}

const isPrescriptionFilled = (rx: Prescription): boolean =>
  Boolean(rx.filledBy ?? rx.filledById)

export const getPrescriptionMostRecentDate = (
  rx: Prescription,
): string | null =>
  rx.modificationDate ??
  rx.updatedAt ??
  rx.creationDate ??
  (rx as { createdAt?: string }).createdAt ??
  null

const PrescriptionFields = {
  drugExpirationDate: (rx: Prescription): NullableString => {
    const flat = rx as {
      quantityDrugExpirationDate?: string | null
    }
    return flat.quantityDrugExpirationDate ?? null
  },
  refillExpirationDate: (rx: Prescription): NullableString => {
    const flat = rx as {
      quantityRefillExpirationDate?: string | null
    }
    return flat.quantityRefillExpirationDate ?? null
  },

  lastFillDate: (rx: Prescription, isInHouse = false): string | null => {
    const all = [rx, ...(rx.refills ?? [])]
    const dates = all
      .filter((p) => isInHouse || isPrescriptionFilled(p))
      .map(getPrescriptionMostRecentDate)
      .filter((date): date is string => date !== null)

    return dates.length > 0
      ? ([...dates].sort((a, b) => a.localeCompare(b)).pop() ?? null)
      : null
  },

  numberOfRefills: (rx: Prescription): number | null =>
    rx.quantityNumbRefills ?? null,

  refillsRemaining: (rx: Prescription): number => getRemainingRefillsCount(rx),
}

const toStringOrZero = (value: number | null | undefined): string =>
  value === null || value === undefined ? '0' : String(value)

const getFulfillmentText = (prescriptionType: PrescriptionType): string => {
  switch (prescriptionType) {
    case PrescriptionType.CHEWY:
      return i18n.t('Common:CHEWY_COM')
    case PrescriptionType.IN_HOUSE:
      return i18n.t('Common:IN_HOUSE')
    case PrescriptionType.SCRIPT_OUT:
      return i18n.t('Common:OUTSIDE_PHARMACY')
    default:
      return ''
  }
}

const getLastFill = (rx: Prescription, isInHouse = false): string => {
  const lastFillDate = PrescriptionFields.lastFillDate(rx, isInHouse)
  return DateFormatters.date(lastFillDate)
}

const buildFields = (
  conditionalFields: Array<DisplayField | null | false>,
): DisplayField[] => conditionalFields.filter(Boolean) as DisplayField[]

const getOrderDate = (rx: Prescription): NullableString =>
  rx.creationDate ?? (rx as { createdAt?: string }).createdAt ?? null

const getCustomViewFields = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  opts: GetRxFieldsOptions,
): DisplayField[] => {
  const {
    prescriptionProductSizeUnit = [],
    productSizeUnit = [],
    vetDietConstants,
  } = opts
  const hasUnits =
    prescriptionProductSizeUnit.length > 0 || productSizeUnit.length > 0

  const orderDate = getOrderDate(rx)
  const isOutsidePharmacy = prescriptionType === PrescriptionType.SCRIPT_OUT
  const qtyDisplay = hasUnits
    ? getQtyRequested(rx, {
        includeRefills: !isOutsidePharmacy,
        prescriptionProductSizeUnit,
        productSizeUnit,
        quantityAndUnitOnly: isOutsidePharmacy,
        vetDietConstants,
      })
    : null

  return buildFields([
    {
      label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.ORDER_DATE'),
      value: formatDateOrPlaceholder(orderDate, DateFormatters.dateTime),
    },

    prescriptionType
      ? {
          label: i18n.t('Common:FULFILLMENT'),
          value: getFulfillmentText(prescriptionType),
        }
      : null,

    hasUnits && qtyDisplay
      ? {
          label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.QTY_REQUESTED'),
          value: qtyDisplay.primary,
          secondaryText: qtyDisplay.secondary,
        }
      : null,
  ])
}

const getExpirationField = (rx: Prescription): DisplayField | null => {
  const expirationDate = PrescriptionFields.refillExpirationDate(rx)
  return {
    label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.EXPIRATION'),
    value: formatDateValueOrPlaceholder(expirationDate, DateFormatters.date),
  }
}

const getLastFillField = (
  rx: Prescription,
  prescriptionType: PrescriptionType,
): DisplayField | null => {
  const isInHouse = prescriptionType === PrescriptionType.IN_HOUSE
  const lastFill = getLastFill(rx, isInHouse)

  return {
    boldValue: !lastFill,
    label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.LAST_FILL'),
    value:
      lastFill ||
      (isInHouse
        ? EMPTY_FIELD_PLACEHOLDER
        : i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.IN_PROGRESS')),
  }
}

const getStandardFields = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  opts?: GetRxFieldsOptions,
): DisplayField[] => {
  const resolvedType = prescriptionType ?? PrescriptionType.IN_HOUSE
  const isScriptOut = prescriptionType === PrescriptionType.SCRIPT_OUT

  return buildFields([
    getExpirationField(rx),

    {
      label: i18n.t('Common:FULFILLMENT'),
      value: getFulfillmentText(resolvedType),
    },

    {
      label: i18n.t(
        'Dialogs:PRESCRIPTION_DIALOG.QUANTITY_SECTION.REFILLS_QUANTITY',
      ),
      value: toStringOrZero(PrescriptionFields.numberOfRefills(rx)),
    },

    !isScriptOut && {
      label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.REFILLS_REMAINING'),
      value: toStringOrZero(PrescriptionFields.refillsRemaining(rx)),
      onClick:
        opts?.onDispenseRefill && PrescriptionFields.refillsRemaining(rx) > 0
          ? opts.onDispenseRefill
          : undefined,
      tooltipText:
        opts?.onDispenseRefill && PrescriptionFields.refillsRemaining(rx) > 0
          ? i18n.t('Common:DISPENSE_REFILL')
          : undefined,
    },

    isScriptOut ? null : getLastFillField(rx, resolvedType),
  ])
}

export const getRxFields = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  options: GetRxFieldsOptions = {},
): DisplayField[] => {
  if (options.isOrderedDrug) {
    return buildFields([
      getExpirationField(rx),
      {
        label: i18n.t('Common:FULFILLMENT'),
        value: i18n.t(
          'Clients:TIMELINE.RX_STATUS_TRACKER.ADMINISTERED_IN_HOUSE',
        ),
      },
      getLastFillField(rx, PrescriptionType.IN_HOUSE),
    ])
  }

  const { prescriptionStates } = options

  const flags = getPrescriptionFlags(rx, prescriptionType, prescriptionStates)
  const suppressStandardFields = shouldSuppressFields(flags)

  return suppressStandardFields
    ? getCustomViewFields(rx, prescriptionType, options)
    : getStandardFields(rx, prescriptionType, options)
}

export const getInstructionsField = (rx: Prescription): DisplayField => ({
  label: i18n.t('Common:INSTRUCTION_PRINTED_ON_LABEL'),
  value: formatValueOrPlaceholder(rx.notes ?? ''),
})

export const getPrescriptionStateName = (
  rx: Prescription,
  prescriptionStates?: Constant[],
): string | null => getPrescriptionFlags(rx, null, prescriptionStates).stateName

/**
 * Checks if a prescription is a vet diet prescription.
 */
export const isVetDietPrescription = (prescription: Prescription): boolean =>
  prescription.rxType === RxType.VetDiet

/**
 * Checks if a prescription was dispensed from inventory.
 * Returns true for inventory orders or in-house prescriptions not from vet origin.
 */
export const isDispensedFromInventory = (prescription: {
  origin?: string | null | RxPrescriptionOrigin
  prescriptionType?: string | PrescriptionType
  type?: string
}): boolean => {
  const prescriptionTypeValue = getPrescriptionType(
    prescription.prescriptionType as PrescriptionType | undefined,
  )
  return (
    prescription.type === OrderType.INVENTORY ||
    (prescriptionTypeValue.isInHouse &&
      prescription.origin !== RxPrescriptionOrigin.Vet)
  )
}

/**
 * Checks if a field label matches the QTY_REQUESTED label.
 */
export const isQtyRequestedField = (
  fieldLabel: string,
  qtyRequestedLabel: string,
): boolean => fieldLabel === qtyRequestedLabel

export const getOrderFillStatusText = (
  rx: Prescription,
  prescriptionType?: PrescriptionType,
): string => {
  const fillDate = getPrescriptionMostRecentDate(rx)

  if (
    isDispensedFromInventory({
      ...rx,
      prescriptionType: prescriptionType ?? rx.prescriptionType,
    })
  ) {
    return i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.FILL_STATUS_FILLED', {
      date: DateFormatters.date(fillDate),
    })
  }

  return i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.FILL_STATUS_COMPLETED', {
    date: DateFormatters.date(fillDate),
  })
}

const formatOrderTableQuantity = (
  rx: Prescription,
  parentPrescription: Prescription | undefined,
  opts: OrderTableFieldRowOptions,
  options: { isChewy: boolean },
): string => {
  const qtyResult = getQtyRequested(rx, {
    includeRefills: false,
    parentRx: parentPrescription,
    prescriptionProductSizeUnit: opts.prescriptionProductSizeUnit,
    productSizeUnit: opts.productSizeUnit,
    quantityAndUnitOnly: !options.isChewy,
    usePerRefillQuantity: true,
    vetDietConstants: opts.vetDietConstants,
  })
  return qtyResult.primary
}

interface GetOrderTableRowOptions extends OrderTableFieldRowOptions {
  fillIndex: number
  isInHouse: boolean
  isOrderedDrug: boolean
  /** For qty cell: use when row is refill so parent has partNumber/chewyItems. */
  parentForQty?: Prescription
  parentPrescription: Prescription | undefined
  prescriptionType: NullablePrescriptionType
  /** For qty cell: use when loaded rx has partNumber/chewyItems (Chewy). */
  qtyRx?: Prescription
  timezone: string
  totalFills: number
}

const getOrderTableRow = (
  rx: Prescription,
  options: GetOrderTableRowOptions,
): OrderTableRow[] => {
  const {
    fillIndex,
    isOrderedDrug,
    parentForQty,
    parentPrescription,
    prescriptionType,
    qtyRx,
    timezone,
    totalFills,
    ...opts
  } = options
  const isChewy = prescriptionType === PrescriptionType.CHEWY
  const orderDate = getOrderDate(rx)

  const orderDateCell: OrderTableRow = {
    label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.ORDER_DATE'),
    value: formatDateValueOrPlaceholder(orderDate, DateFormatters.dateTime),
    prescription: rx,
    parentPrescription,
    relativeTime: computeRelativeTime(orderDate, timezone),
  }
  const qtyRequestedCell: OrderTableRow = {
    label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.QTY_REQUESTED'),
    singleLine: true,
    value: formatValueOrPlaceholder(
      formatOrderTableQuantity(
        qtyRx ?? rx,
        parentForQty ?? parentPrescription,
        opts,
        { isChewy },
      ),
    ),
    prescription: rx,
    parentPrescription,
  }
  const fillNumberCell: OrderTableRow = {
    label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.FILL_NUMBER'),
    value: `${fillIndex} / ${totalFills}`,
  }

  if (isChewy) {
    return [orderDateCell, qtyRequestedCell, fillNumberCell]
  }

  const fillStatus = getOrderFillStatusText(rx, prescriptionType ?? undefined)
  const fillStatusCell: OrderTableRow = {
    label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.FILL_STATUS'),
    value: formatValueOrPlaceholder(fillStatus),
  }

  if (isOrderedDrug) {
    return [orderDateCell, qtyRequestedCell, fillNumberCell, fillStatusCell]
  }

  const expirationDate = PrescriptionFields.drugExpirationDate(rx)

  return [
    orderDateCell,
    qtyRequestedCell,
    fillNumberCell,
    {
      label: i18n.t('Common:DRUG_EXPIRATION'),
      value: formatDateValueOrPlaceholder(expirationDate, DateFormatters.date),
    },
    fillStatusCell,
  ]
}

const getCardHeaderForPrescription = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
): string | null => {
  if (prescriptionType === PrescriptionType.CHEWY) {
    return i18n.t('Common:CHEWY_COM')
  }

  const isOrderedDrug = (rx as { type?: string }).type === OrderType.INVENTORY
  if (isOrderedDrug) {
    return i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.ADMINISTERED_IN_HOUSE')
  }

  if (prescriptionType === PrescriptionType.IN_HOUSE) {
    return rx.origin === RxPrescriptionOrigin.Vet
      ? i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.ADMINISTERED_IN_HOUSE')
      : i18n.t('Common:DISPENSED_FROM_INVENTORY')
  }

  return null
}

export const getOrderTableSubHeader = getCardHeaderForPrescription

/**
 * True when the card header should show Order number and P-# (prescription/recipe number).
 * Only for Chewy prescriptions.
 */
export const shouldShowOrderAndPrescriptionNumbers = (
  prescriptionType: NullablePrescriptionType,
): boolean => prescriptionType === PrescriptionType.CHEWY

/**
 * Labels for Chewy order header: "Order number" and "P-#" (or "Recipe number" for vet diet).
 */
export const getChewyOrderInfoLabels = (prescription: Prescription) => {
  const { isVetDiet } = getRxType(prescription)
  return {
    orderNumber: i18n.t('Common:ORDER_NUMBER'),
    prescriptionNumber: isVetDiet
      ? i18n.t('Dialogs:PRESCRIPTION_DIALOG.CHEWY_RECIPE_NUMBER')
      : i18n.t('Dialogs:PRESCRIPTION_DIALOG.CHEWY_PRESCRIPTION_NUMBER'),
  }
}

/** Returns the prescription from a row, optionally filtered by refill status. */
export const getPrescriptionFromRow = <
  T extends { parentPrescription?: Prescription; prescription?: Prescription },
>(
  row: T[],
  isRefill?: boolean,
): Prescription | undefined => {
  const prescription = row.find((cell) => cell.prescription)?.prescription

  if (isRefill === undefined) return prescription

  const hasParent = row.some((cell) => cell.parentPrescription)
  const shouldReturnPrescription = hasParent === isRefill

  return shouldReturnPrescription ? prescription : undefined
}

export interface OrderTableCardHeader {
  orderNumber?: string | null
  rxNo?: string | null
  /** When true, show Order number and P-# (prescription number) in the card header. */
  showOrderAndPrescriptionNumbers?: boolean
  status: OrderDisplayStatus | null
  text: string | null
}

export interface OrderTableContent {
  cardHeaders: OrderTableCardHeader[]
  headers: string[]
  rows: OrderTableRow[][]
}

/** CVC-35572: Shipping shows '--' when row has no order number and no prescription number. */
export const appendShippingColumn = (
  content: OrderTableContent,
  shippingTexts: Array<string | string[]>,
  shippingHeader: string,
): OrderTableContent => {
  const preparingLabel = i18n.t(
    'Clients:TIMELINE.RX_STATUS_TRACKER.PREPARING_FOR_SHIPMENT',
  )
  const isEmpty = (v?: string | null) =>
    !v?.trim() || v === EMPTY_FIELD_PLACEHOLDER

  const headers = [...content.headers, shippingHeader]
  const rows = content.rows.map((row, index) => {
    const header = content.cardHeaders[index]
    const hasOrderOrRx =
      Boolean(header) && (!isEmpty(header.orderNumber) || !isEmpty(header.rxNo))
    const raw = hasOrderOrRx
      ? (shippingTexts[index] ?? preparingLabel)
      : EMPTY_FIELD_PLACEHOLDER
    const value = Array.isArray(raw)
      ? raw.filter(Boolean).join('\n')
      : String(raw)

    return [
      ...row,
      {
        label: shippingHeader,
        value,
      },
    ]
  })
  return { ...content, headers, rows }
}

export function hasOrderTable(
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
): boolean {
  const isChewy = prescriptionType === PrescriptionType.CHEWY
  const isInHouse = prescriptionType === PrescriptionType.IN_HOUSE
  const isOrderedDrug = (rx as { type?: string }).type === OrderType.INVENTORY
  return isChewy || isInHouse || isOrderedDrug
}

export interface GetOrderTableFieldsOptions extends OrderTableFieldRowOptions {
  isOrderedDrug?: boolean
  /** When set (e.g. Chewy), use for qty requested so partNumber/chewyItems are available. */
  orderTableLoadedRx?: PrescriptionV2 | null
  timezone: string
}

export const getOrderTableFields = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  options: GetOrderTableFieldsOptions,
): OrderTableContent | null => {
  if (!hasOrderTable(rx, prescriptionType)) return null
  const isInHouse = prescriptionType === PrescriptionType.IN_HOUSE
  const isOrderedDrug =
    options.isOrderedDrug ??
    (rx as { type?: string }).type === OrderType.INVENTORY
  const { orderTableLoadedRx, timezone, ...opts } = options
  const loadedSameAsRx =
    orderTableLoadedRx?.id != null && orderTableLoadedRx.id === rx.id
  /** Use loaded rx when same as card rx so refills array is used (e.g. Chewy). */
  const rxForTable =
    loadedSameAsRx && orderTableLoadedRx ? orderTableLoadedRx : rx

  const refills = (rxForTable.refills ?? []).filter(
    (r): r is Prescription => r != null,
  )
  /** Total fills = original (1) + allowed refills. Unfilled refills don't appear in array yet. Same as in-house. */
  const rxForTableWithRefills = rxForTable as Prescription & {
    numberRefills?: number
    quantityNumbRefills?: number
  }
  const numRefillsAllowed = Number(
    rxForTableWithRefills.quantityNumbRefills ??
      rxForTableWithRefills.numberRefills ??
      0,
  )
  /** Use at least the number of rows we render so fill index never exceeds total (avoids "4 / 3" when refills.length > numRefillsAllowed). */
  const totalFills = Math.max(numRefillsAllowed + 1, refills.length + 1)

  const baseRowOptions: Omit<
    GetOrderTableRowOptions,
    'fillIndex' | 'parentForQty' | 'parentPrescription' | 'qtyRx'
  > = {
    ...opts,
    isInHouse: isInHouse || isOrderedDrug,
    isOrderedDrug,
    prescriptionType:
      prescriptionType ??
      (isOrderedDrug
        ? (PrescriptionType.IN_HOUSE as NullablePrescriptionType)
        : null),
    timezone,
    totalFills,
  }

  const convertedLoadedRx =
    orderTableLoadedRx != null
      ? convertPrescriptionV2ToPrescription(orderTableLoadedRx)
      : undefined

  const firstRow = getOrderTableRow(rx, {
    ...baseRowOptions,
    fillIndex: 1,
    parentForQty: undefined,
    parentPrescription: undefined,
    qtyRx: loadedSameAsRx ? convertedLoadedRx : undefined,
  })

  /** Sort by date ascending (oldest first), assign fill 1, 2, 3… by order filled. Then reverse so table shows newest first (e.g. 3/4, 2/4, 1/4 when 2 refills filled). */
  const byDateAsc = [rx, ...refills].sort((a, b) => {
    const dateA = getPrescriptionMostRecentDate(a) ?? ''
    const dateB = getPrescriptionMostRecentDate(b) ?? ''
    return dateA.localeCompare(dateB)
  })
  const withFillIndex = byDateAsc.map((p, i) => ({
    prescription: p,
    fillIndex: i + 1,
  }))
  const allPrescriptions = withFillIndex.slice().reverse()

  const rows: OrderTableRow[][] = allPrescriptions.map(
    ({ prescription: p, fillIndex }) => {
      const prescription = p
      const isParent = prescription.id === rx.id
      return getOrderTableRow(prescription, {
        ...baseRowOptions,
        fillIndex,
        parentForQty: isParent ? undefined : (convertedLoadedRx ?? rx),
        parentPrescription: isParent ? undefined : rx,
        qtyRx: isParent && loadedSameAsRx ? convertedLoadedRx : undefined,
      })
    },
  )

  const headers = firstRow.map((field) => field.label)
  const parentHeader = getCardHeaderForPrescription(rx, prescriptionType)

  const cardHeaders = allPrescriptions.map(({ prescription: pres }) => {
    const prescription = pres
    const status = getOrderDisplayStatus({
      prescription,
      prescriptionStates: opts.prescriptionStates,
    })

    const isParent = prescription.id === rx.id
    const headerPrescriptionType = isParent
      ? prescriptionType
      : (
          prescription as Prescription & {
            prescriptionType?: NullablePrescriptionType
          }
        ).prescriptionType
    const showOrderAndPrescriptionNumbers =
      shouldShowOrderAndPrescriptionNumbers(headerPrescriptionType)

    if (isParent) {
      return {
        text: parentHeader ?? i18n.t('Common:ORDER'),
        status,
        orderNumber: rx.orderNumber ?? EMPTY_FIELD_PLACEHOLDER,
        rxNo: rx.rxNo ?? EMPTY_FIELD_PLACEHOLDER,
        ...(showOrderAndPrescriptionNumbers
          ? { showOrderAndPrescriptionNumbers: true }
          : {}),
      }
    }

    const refillHeader = getCardHeaderForPrescription(
      prescription,
      (
        prescription as Prescription & {
          prescriptionType?: NullablePrescriptionType
        }
      ).prescriptionType,
    )

    const dispensedFromInventory = i18n.t('Common:DISPENSED_FROM_INVENTORY')
    const text =
      refillHeader === dispensedFromInventory
        ? i18n.t('Clients:TIMELINE.PRESCRIPTION_CARD.REFILL_DISPENSED')
        : (refillHeader ?? parentHeader ?? i18n.t('Common:ORDER'))

    return {
      text,
      status,
      orderNumber: prescription.orderNumber ?? EMPTY_FIELD_PLACEHOLDER,
      rxNo: prescription.rxNo ?? EMPTY_FIELD_PLACEHOLDER,
      ...(showOrderAndPrescriptionNumbers
        ? { showOrderAndPrescriptionNumbers: true }
        : {}),
    }
  })

  return {
    cardHeaders,
    headers,
    rows,
  }
}

export const getPrescriptionTitle = (
  prescription: Prescription,
  item?: RxCardItem,
  includeType?: boolean,
): string => {
  const baseTitle =
    item?.prescription?.name ??
    item?.prescription?.masterName ??
    LanguageUtils.getTranslatedFieldName(prescription)

  if (includeType) {
    const prescriptionTypeLabel = getPrescriptionTypeLabel({
      prescriptionType: prescription.prescriptionType,
      origin: prescription.origin,
    })
    return `${prescriptionTypeLabel}–${baseTitle}`
  }

  return baseTitle
}

/**
 * Gets the prescription timeline item with origin data merged into prescription.
 */
export const getPrescriptionWithOrigin = (
  item: PrescriptionTimelineItem,
): PrescriptionTimelineItem => ({
  ...item,
  prescription: {
    ...item.prescription,
    origin: item.origin,
  },
})

/**
 * Prepares a prescription for the refill dialog by omitting specified fields.
 * Prevents duplicate charges and incorrect data from being copied to refills.
 *
 * @param prescription - The prescription to prepare
 * @param fieldsToOmit - Prescription keys to omit (e.g., 'id', 'soapId'). 'retailOrderLineItem' is always omitted automatically.
 * @returns An object with cleaned prescription and parent for the refill dialog
 */
export const prepareRefillPrescription = (
  prescription: Prescription,
  fieldsToOmit: (keyof Prescription)[],
): { parent: Prescription; prescription: Prescription } => {
  // Always omit retailOrderLineItem to prevent duplicate charges
  const fieldsToOmitSet = new Set([...fieldsToOmit, 'retailOrderLineItem'])
  const prescriptionFieldsToOmit = Array.from(fieldsToOmitSet)
  // Parent doesn't need 'id' omitted
  const parentFieldsToOmit = prescriptionFieldsToOmit.filter((f) => f !== 'id')

  return {
    prescription: R.omit(
      prescriptionFieldsToOmit,
      prescription,
    ) as Prescription,
    parent: R.omit(parentFieldsToOmit, prescription) as Prescription,
  }
}
